import {Formik, Field} from "formik";
import {Button, Modal} from "react-bootstrap";
import * as Yup from "yup";
import {useCallback, useRef, useState} from "react";
import ImageComponent from "../shared/ImageComponent";
import NoImage from "../../../images/NoImage.jpg";
import profileImage from "../../../images/profileImage.png";
import UpImage from "../../../images/image-upload.svg";
import {useUpdateIntervention} from "../../../hooks/useInterventions";
import {documentType, fileTypeRelation} from "../../../utils/constants";
import {useAddFile} from "../../../hooks/useFile";
import {buildImageUrl} from "../../../utils/imageUrlBuilder";
import {useAddReview} from "../../../hooks/useReview";
import fileImage from "../../../images/fileImage.png";
import {showToastModal} from "../shared/ToastModalSwal";
import {
  combineFrenchDateAndTime,
  parseFrenchDate,
} from "../../../utils/date";
import {
  FrenchDateField,
  FrenchTimeField,
} from "../shared/FrenchDateTimeFields";
import UploadPreviewState from "../shared/UploadPreviewState";
import {buildInterventionClosureBody} from "../../../utils/additionalPrice";

const MAX_CLOSURE_FILE_SIZE = 10 * 1024 * 1024;
const MAX_CLOSURE_FILES = 5;
const CLOSURE_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
]);

const fluxChoices = [
  {title: "Mission accomplie"},
  {title: "Terminer et ajouter des frais supplémentaires"},
  {title: "Planifier une intervention complémentaire"},
];

const InterventionModal = ({openModal, toggleModal, intervention}) => {
  const {
    onCreateNextIntervention,
    onFinalizeIntervention,
  } = useUpdateIntervention();
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const {onAdd} = useAddFile();
  const inputRef = useRef();
  const [step, setStep] = useState("intervention");
  const {onAddReview} = useAddReview();
  const avisUser = intervention?.Offer_Proposal?.Offer
    ? intervention?.Offer_Proposal?.Offer?.Client?.User
    : intervention?.Offer_Proposal?.directChat?.chatroom?.Client?.User;

  const handleDiplome = useCallback(
    (e) => {
      const file = e.target.files[0];
      e.target.value = "";
      if (file) {
        if (!CLOSURE_FILE_TYPES.has(file.type)) {
          showToastModal({
            success: false,
            title: "Format non accepté",
            message: "Ajoutez une image (JPG, PNG, GIF ou WebP) ou un document PDF.",
            buttonTitle: "Fermer",
          });
          return;
        }
        if (file.size > MAX_CLOSURE_FILE_SIZE) {
          showToastModal({
            success: false,
            title: "Fichier trop volumineux",
            message: "Chaque pièce justificative doit peser 10 Mo maximum.",
            buttonTitle: "Fermer",
          });
          return;
        }
        if (files.length >= MAX_CLOSURE_FILES) {
          showToastModal({
            success: false,
            title: "Limite atteinte",
            message: "Vous pouvez ajouter au maximum 5 pièces justificatives.",
            buttonTitle: "Fermer",
          });
          return;
        }
        const imageUrl = URL.createObjectURL(file);
        setImages((prev) => [...prev, {imageUrl, file}]);
        const formData = new FormData();
        formData.append("fileUpload", file);
        formData.append("type", documentType.ATTACHMENT);
        formData.append("fileUploadId", intervention?.id);
        formData.append("fileUploadType", fileTypeRelation.intervention);
        setFiles((prev) => [...prev, formData]);
      }
    },
    [files.length, intervention?.id],
  );

  const handleDelete = useCallback((index) => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.imageUrl) URL.revokeObjectURL(removed.imageUrl);
      return prev.filter((_, i) => i !== index);
    });
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formSchema = Yup.object().shape({
    selectedFlux: Yup.number()
      .required("Veuillez sélectionner une option")
      .min(0, "Sélection invalide"),
    description: Yup.string()
      .required("Description est requise")
      .min(2, "La description doit contenir au moins 2 caractères"),
    prixSup: Yup.number()
      .nullable()
      .when("selectedFlux", {
        is: 1,
        then: (schema) =>
          schema
            .typeError("Le prix supplémentaire doit être un nombre")
            .required("Le prix supplémentaire est requis")
            .min(1, "Le prix doit être supérieur ou égal à 1 €")
            .max(100000, "Le prix supplémentaire ne peut pas dépasser 100 000 €"),
      }),
    initialPrice: Yup.number()
      .nullable()
      .when("selectedFlux", {
        is: 2,
        then: (schema) =>
          schema
            .typeError("Le prix initial doit être un nombre")
            .required("Le prix initial est requis")
            .min(1, "Le prix doit être supérieur ou égal à 1 €"),
      }),
    title: Yup.string()
      .nullable()
      .when("selectedFlux", {
        is: 2,
        then: (schema) =>
          schema
            .typeError("Le titre doit être un texte")
            .required("Le titre est requis")
            .min(2, "Le titre doit contenir au moins 2 caractères"),
      }),
    appointmentDate: Yup.string()
      .nullable()
      .when("selectedFlux", {
        is: 2,
        then: (schema) =>
          schema
            .required("La date du RDV est requise")
            .test(
              "is-valid-date",
              "Date du RDV invalide (format attendu : jj/mm/aaaa)",
              (value) => Boolean(parseFrenchDate(value)),
            ),
      }),
    appointmentTime: Yup.string()
      .nullable()
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Format HH:mm requis")
      .when("selectedFlux", {
        is: 2,
        then: (schema) =>
          schema
            .required("L'heure du RDV est requise")
            .test(
              "is-future-date-time",
              "La date et l'heure du RDV doivent être ultérieures à maintenant",
              function (value) {
                const appointment = combineFrenchDateAndTime(
                  this.parent.appointmentDate,
                  value,
                );
                return !appointment || appointment.getTime() > Date.now();
              },
            ),
      }),
  });
  const handleReviewSubmit = async (values) => {
    // Add your review submission logic here
    onAddReview({
      comment: values.comment,
      rating: values.rating,
      createdForId: avisUser?.id,
      interventionId: intervention?.id,
    });
    toggleModal(false);
    setStep("intervention");
  };

  return (
    <Modal
      className="modal fade "
      show={openModal}
      onHide={toggleModal}
      centered
      size={step === "intervention" ? "lg" : "md"}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title text-[#1A64B9] font-semibold">
            {step === "intervention"
              ? "Clôturer l'intervention"
              : "Ajouter un avis"}
          </h4>
          <Button
            variant=""
            type="button"
            className="close"
            onClick={() => toggleModal(false)}
          >
            <span>×</span>
          </Button>
        </div>
        <div className="modal-body !py-3 !px-0 h-fit max-h-[72vh] overflow-scroll overflow-style-none">
          {step === "intervention" ? (
            <Formik
              initialValues={{
                selectedFlux: 0,
                prixSup: 1,
                description: "",
                appointmentDate: "",
                appointmentTime: "",
                title: "",
                initialPrice: 0,
              }}
              validationSchema={formSchema}
              onSubmit={async (values, {setSubmitting}) => {
                try {
                  const uploadPromises = files.map((file, index) =>
                    onAdd(file, ({loaded, total}) =>
                      setUploadProgress((current) => {
                        const next = [...current];
                        next[index] = total
                          ? Math.round((loaded / total) * 100)
                          : 0;
                        return next;
                      }),
                    ),
                  );
                  await Promise.all(uploadPromises);

                  if (values.selectedFlux === 0 || values.selectedFlux === 1) {
                    await onFinalizeIntervention({
                      id: intervention.id,
                      body: buildInterventionClosureBody({
                        selectedFlux: values.selectedFlux,
                        description: values.description,
                        additionalPrice: values.prixSup,
                      }),
                    });
                  } else if (values.selectedFlux === 2) {
                  const optionalProp = {};
                  if (intervention?.Offer_Proposal?.directChat) {
                    optionalProp.clientId =
                      intervention.Offer_Proposal.directChat.chatroom.clientId;
                    optionalProp.directChatId =
                      intervention.Offer_Proposal.directChat.id;
                  } else if (
                    intervention?.id &&
                    intervention?.Offer_Proposal.Offer.clientId
                  ) {
                    optionalProp.clientId =
                      intervention.Offer_Proposal.Offer.clientId;
                  } else {
                    setSubmitting(false);
                    return;
                  }
                  const {appointmentDate, appointmentTime} = values;

                  // Combine date and time into ISO 8601 format
                  const combinedDateTime = combineFrenchDateAndTime(
                    appointmentDate,
                    appointmentTime,
                  ).toISOString();
                  await onCreateNextIntervention({
                    interventionId: intervention.id,
                    nextProposal: {
                      initialBudget: values.initialPrice,
                      appointmentDate: combinedDateTime,
                      title: values.title,
                      ...optionalProp,
                    },
                    report: values.description,
                  });

                  }
                  setStep("review");
                } catch (error) {
                  showToastModal({
                    success: false,
                    title: "Clôture impossible",
                    message:
                      error?.response?.data?.message ||
                      error?.response?.data?.error ||
                      (error instanceof RangeError ? error.message : null) ||
                      "La pièce jointe ou la clôture n'a pas pu être enregistrée. Réessayez.",
                    buttonTitle: "Fermer",
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
                <form className="w-full" onSubmit={handleSubmit}>
                  <div className="w-full flex flex-col justify-start items-start gap-2 !px-6 !pb-4 border-b">
                    {fluxChoices.map((flux, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-start gap-2 cursor-pointer"
                        onClick={() => setFieldValue("selectedFlux", index)}
                      >
                        <input
                          type="radio"
                          checked={values.selectedFlux === index}
                          onChange={() => {}}
                        />
                        <div className="text-base capitalize-first">
                          {flux.title}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="w-full !px-6 !py-3">
                    <div className="text-base font-medium text-left capitalize-first text-[#19295C] !mb-2">
                      Rapport
                    </div>

                    <div className="w-full p-2 flex flex-col justify-center">
                      {values.selectedFlux === 1 && (
                        <div className="form-group !mb-4 lg:w-1/2">
                          <label className="text-sm font-medium leading-3 text-[#7E7E7E]">
                            Prix supplémentaire
                          </label>
                          <div className="flex items-center w-full px-3 py-2 border rounded-md bg-white">
                            <Field
                              type="number"
                              className="border-none flex-grow"
                              name="prixSup"
                              min="1"
                              max="100000"
                            />
                            <span className="text-[#1A64B9]">€</span>
                          </div>
                          {errors.prixSup && touched.prixSup && (
                            <div
                              className="text-red-500 text-sm mt-1"
                              style={{display: "block"}}
                            >
                              {errors.prixSup}
                            </div>
                          )}
                        </div>
                      )}

                      {values.selectedFlux === 2 && (
                        <>
                          <div className="form-group !mb-4 ">
                            <label className="text-sm font-medium leading-4 sm:leading-3 text-[#7E7E7E]">
                              Titre de la nouvelle intervention
                            </label>
                            <Field
                              type="text"
                              className="form-control"
                              name="title"
                            />

                            {errors.initialPrice && touched.initialPrice && (
                              <div
                                className="text-red-500 text-sm mt-1"
                                style={{display: "block"}}
                              >
                                {errors.initialPrice}
                              </div>
                            )}
                          </div>
                          <div className="form-group !mb-4 lg:w-1/2">
                            <label className="text-sm font-medium leading-3 text-[#7E7E7E]">
                              Prix initiale
                            </label>
                            <div className="flex items-center w-full px-3 py-2 border rounded-md bg-white">
                              <Field
                                type="number"
                                className="border-none flex-grow"
                                name="initialPrice"
                                min="0"
                              />
                              <span className="text-[#1A64B9]">€</span>
                            </div>
                            {errors.initialPrice && touched.initialPrice && (
                              <div
                                className="text-red-500 text-sm mt-1"
                                style={{display: "block"}}
                              >
                                {errors.initialPrice}
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1  sm:grid-cols-2 gap-2 !mb-4">
                            <div className="form-group">
                              <label className="text-sm font-medium leading-3 text-[#7E7E7E]">
                                Date du RDV
                              </label>
                              <FrenchDateField
                                className="form-control"
                                name="appointmentDate"
                              />
                              {errors.appointmentDate &&
                                touched.appointmentDate && (
                                  <div
                                    className="text-red-500 text-sm mt-1"
                                    style={{display: "block"}}
                                  >
                                    {errors.appointmentDate}
                                  </div>
                                )}
                            </div>
                            <div className="form-group">
                              <label className="text-sm font-medium leading-3 text-[#7E7E7E]">
                                Heure du RDV
                              </label>
                              <FrenchTimeField
                                className="form-control"
                                name="appointmentTime"
                              />
                              {errors.appointmentTime &&
                                touched.appointmentTime && (
                                  <div
                                    className="text-red-500 text-sm mt-1"
                                    style={{display: "block"}}
                                  >
                                    {errors.appointmentTime}
                                  </div>
                                )}
                            </div>
                          </div>
                        </>
                      )}

                      <div className="form-group !mb-4">
                        <label className="text-sm font-medium leading-3 text-[#7E7E7E]">
                          Description
                        </label>
                        <Field
                          as="textarea"
                          rows={3}
                          className="form-control"
                          name="description"
                        />
                        {errors.description && touched.description && (
                          <div
                            className="text-red-500 text-sm mt-1"
                            style={{display: "block"}}
                          >
                            {errors.description}
                          </div>
                        )}
                      </div>

                      <ImageUploadSection
                        handleDelete={handleDelete}
                        handleDiplome={handleDiplome}
                        images={images}
                        inputRef={inputRef}
                        uploadProgress={uploadProgress}
                        uploading={isSubmitting}
                      />

                      <div className="text-center mt-4">
                        <button
                          data-testid="intervention-finalize-submit"
                          type="submit"
                          disabled={isSubmitting}
                          className=" !p-0 w-full h-[47px] mt-2 rounded-lg text-sm leading-4 gap-3 uppercase font-semibold flex justify-center items-center bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white "
                        >
                          {/* {fluxChoices[values.selectedFlux].title}
                           */}
                          Clôturer
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          ) : (
            <div className="w-full flex flex-col justify-center items-center gap-2 ">
              <div className="flex flex-col justify-center items-center ">
                <ImageComponent
                  src={buildImageUrl(avisUser?.imageUri)}
                  alt="User"
                  className="w-16 aspect-square rounded-full object-cover"
                  fallback={
                    <img
                      src={profileImage}
                      alt="User"
                      className="w-16 aspect-square rounded-full object-cover"
                    />
                  }
                />
                <div className="text-base font-semibold text-[#19295C] capitalize">
                  {avisUser?.lastName} {avisUser?.firstName}
                </div>
                <div
                  className="text-base font-normal  
                "
                >
                  {intervention?.title}{" "}
                </div>
              </div>

              <Formik
                initialValues={{rating: 0, comment: ""}}
                validationSchema={Yup.object({
                  rating: Yup.number()
                    .required("Le rating est requis")
                    .min(1, "Le rating doit être au moins 1")
                    .max(5, "Le rating ne peut pas dépasser 5"),
                  comment: Yup.string()
                    .required("Le commentaire est requis")
                    .min(
                      5,
                      "Le commentaire doit contenir au moins 5 caractères",
                    ),
                })}
                onSubmit={handleReviewSubmit}
              >
                {({values, errors, touched, handleSubmit, setFieldValue}) => (
                  <form onSubmit={handleSubmit} className="w-full">
                    <div className="w-full !px-6 !py-3">
                      <div className="form-group !mb-4">
                        <div className="flex w-full justify-center items-center gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              type="button"
                              key={star}
                              onClick={() => setFieldValue("rating", star)}
                              className={`text-4xl ${
                                values.rating >= star
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        {errors.rating && touched.rating && (
                          <div className="text-red-500 text-sm mt-1">
                            {errors.rating}
                          </div>
                        )}
                      </div>

                      <div className="form-group !mb-4">
                        <Field
                          as="textarea"
                          rows={3}
                          className="form-control placeholder:text-sm"
                          name="comment"
                          placeholder="Votre commentaire ici..."
                        />
                        {errors.comment && touched.comment && (
                          <div className="text-red-500 text-sm mt-1">
                            {errors.comment}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => toggleModal(false)}
                          className="!rounded-md  !py-3 bg-white !text-[#0D44B1] !border-[#0D44B1] !border font-medium text-sm   uppercase"
                        >
                          Ignorer
                        </button>
                        <button
                          type="submit"
                          className="!rounded-md  !py-3 text-white bg-gradient-to-r from-[#0022AA] to-[#1A64B9] font-medium text-sm    uppercase"
                        >
                          Soumettre l'avis
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const ImageUploadSection = ({
  images,
  handleDiplome,
  handleDelete,
  inputRef,
  uploadProgress,
  uploading,
}) => (
  <div className="flex flex-col w-full justify-start gap-3">
    <div className="font-semibold text-sm">Pièces justificatives</div>
    <div className="text-xs text-[#7E7E7E]">
      Images ou documents PDF — 10 Mo maximum par fichier, 5 fichiers maximum.
    </div>
    <div className="grid grid-cols-5 w-full gap-2">
      <div className="col-span-5 lg:col-span-2">
        <div
          onClick={() => inputRef.current.click()}
          className="flex flex-col px-3 py-3 justify-center items-center w-full min-w-[200px] border-2 border-[#1A64B9] border-dashed rounded-xl cursor-pointer"
        >
          <img src={UpImage} alt="upload" className="w-auto h-[40px] mb-2" />
          <h3 className="font-semibold text-center w-[150px] text-xs text-[#19295C]">
            Déposez une image ou un document PDF
          </h3>
          <div className="font-semibold text-center w-full text-[10px] text-[#19295C]">
            ou{" "}
            <span className="text-[#FF6600]">
              parcourez vos fichiers
            </span>{" "}
          </div>
        </div>
        <input
          type="file"
          ref={inputRef}
          className="hidden"
          onChange={handleDiplome}
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        />
      </div>
      <div className="lg:ml-3 grid grid-cols-3 w-full col-span-5 lg:col-span-3 gap-3">
        {images.map((image, index) => (
              <div
                key={index}
                className="w-full aspect-square bg-white flex flex-col justify-center items-center relative rounded-xl"
              >
                <div
                  onClick={() => handleDelete(index)}
                  className="w-6 h-6 rounded-full flex justify-center items-center bg-[#ffcccc] absolute z-10 top-[10px] right-[10px] text-[16px] cursor-pointer text-[#FF0000]"
                >
                  <i className="fi fi-rr-trash flex justify-center items-center"></i>
                </div>
                <UploadPreviewState
                  uploading={uploading}
                  progress={uploadProgress[index] || 0}
                  className="aspect-square"
                >
                  <ImageComponent
                    src={image.file?.type === "application/pdf" ? fileImage : image?.imageUrl}
                    alt="Selected"
                    className="w-full aspect-square object-contain border-2 border-solid rounded-xl"
                    fallback={
                      <img
                        src={NoImage}
                        alt="Selected"
                        className="w-full aspect-square object-cover border-2 border-solid rounded-xl"
                      />
                    }
                  />
                </UploadPreviewState>
                <span className="w-full px-1 text-[10px] text-center truncate" title={image.file?.name}>
                  {image.file?.name}
                </span>
              </div>
        ))}
      </div>
    </div>
  </div>
);

export default InterventionModal;
